import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Small flags as inline SVG (16×12): Excel Desktop on Windows draws flag emoji as two letters ("US"), so the
 *  pane never uses them (PLAN-model-choice.md D4). A code without a drawing shows as its letters. */
const DRAWINGS: Record<string, ReactNode> = {
  US: (
    <>
      <rect width="16" height="12" fill="#fff" />
      {/* 13 stripes, the 7 red ones drawn on white; the canton covers the top 7 */}
      {Array.from({ length: 7 }, (_, k) => (
        <rect key={k} y={(2 * k * 12) / 13} width="16" height={12 / 13} fill="#B22234" />
      ))}
      <rect width="7" height={(7 * 12) / 13} fill="#3C3B6E" />
    </>
  ),
  FR: (
    <>
      <rect width="5.34" height="12" fill="#0055A4" />
      <rect x="5.33" width="5.34" height="12" fill="#fff" />
      <rect x="10.66" width="5.34" height="12" fill="#EF4135" />
    </>
  ),
  EU: (
    <>
      <rect width="16" height="12" fill="#003399" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * Math.PI) / 6;
        return <circle key={i} cx={8 + 3.6 * Math.sin(a)} cy={6 - 3.6 * Math.cos(a)} r="0.55" fill="#FFCC00" />;
      })}
    </>
  ),
};

export function Flag({ code, className = '' }: { code: string; className?: string }) {
  const { t } = useTranslation();
  const name = t(`flag.${code}`, { defaultValue: code });
  const drawing = DRAWINGS[code];
  if (!drawing) {
    return <span title={name} className={`rounded-[2px] border border-line px-[3px] font-mono text-[9px] leading-[11px] text-muted ${className}`}>{code}</span>;
  }
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" role="img" aria-label={name} className={`inline-block shrink-0 overflow-hidden rounded-[2px] ring-1 ring-black/10 ${className}`}>
      <title>{name}</title>
      {drawing}
    </svg>
  );
}
